/**
 * CR-5551: Supplier-side modal to raise a Concession or request a Production
 * Permit directly against a PO line.
 *
 * Spec ask: "Suppliers naturally identify issues against an order, a delivery,
 * or a shipment — not from a standalone quality menu." This modal is the
 * PO-line-driven entry point. The Customer / PO Number / Line / Part Number /
 * Description / Quantity Ordered fields auto-populate from the line so the
 * supplier only fills the deviation-specific bits.
 *
 * The PE/CE distinction is fixed by which button launched the modal — we
 * surface the headline "Production Permit (PE)" or "Concession (CE)" in the
 * dialog title instead of asking the user to pick again.
 */
import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { qualityAPI, PurchaseOrderLine } from '../../lib/api';
import type { ConcessionDeviationType, ConcessionRequestType, ConcessionRiskLevel } from '../../lib/api/types/quality';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface RaiseConcessionFromLineModalProps {
  isOpen: boolean;
  onClose: () => void;
  line: PurchaseOrderLine;
  requestType: ConcessionRequestType;
  onSuccess?: () => void;
}

export function RaiseConcessionFromLineModal({ isOpen, onClose, line, requestType, onSuccess }: RaiseConcessionFromLineModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [proposedDisposition, setProposedDisposition] = useState('');
  const [deviationType, setDeviationType] = useState<ConcessionDeviationType>('dimensional');
  const [riskLevel, setRiskLevel] = useState<ConcessionRiskLevel>('medium');
  const [riskJustification, setRiskJustification] = useState('');
  // Auto-populate quantity from the line — the supplier almost always
  // wants to deviate the whole ordered qty; they can edit if not.
  const [quantityAffected, setQuantityAffected] = useState<string>(
    line.quantity ? String(parseFloat(line.quantity).toFixed(0)) : ''
  );

  const headline = requestType === 'production_permit' ? 'Request Production Permit (PE)' : 'Raise Concession (CE)';
  const isValid = title.trim() && description.trim() && reason.trim() && quantityAffected;

  const handleSubmit = async () => {
    if (!isValid) return;
    if (!line.customer_id || !line.supplier_id) {
      toast.error('PO line is missing customer/supplier — refresh and retry.');
      return;
    }
    try {
      setSubmitting(true);
      // CR-5551: customer + supplier come from the PO line itself. The
      // serializer also expects po_line, po_number, part_number etc. so
      // suppliers don't have to retype anything that the order already
      // tells us.
      await qualityAPI.createConcession({
        customer: line.customer_id,
        supplier: line.supplier_id,
        po_line: line.id,
        po_number: line.po_number,
        part_number: line.part_number,
        part_description: line.description || '',
        quantity_affected: parseInt(quantityAffected, 10),
        deviation_type: deviationType,
        request_type: requestType,
        title: title.trim(),
        description: description.trim(),
        reason: reason.trim(),
        proposed_disposition: proposedDisposition.trim(),
        risk_level: riskLevel,
        risk_justification: riskJustification.trim(),
        status: 'submitted',
      });
      toast.success(`${requestType === 'production_permit' ? 'Production Permit request' : 'Concession'} submitted`);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{headline}</DialogTitle>
          <DialogDescription>
            Submit a deviation request linked to PO {line.po_number} / Line {line.line_number} for customer review.
          </DialogDescription>
        </DialogHeader>

        {/* Auto-populated context (read-only) */}
        <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-md text-sm">
          <div>
            <span className="text-muted-foreground">Customer:</span>{' '}
            <span className="font-medium">{line.customer_name || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Supplier:</span>{' '}
            <span className="font-medium">{line.supplier_name || '-'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">PO / Line:</span>{' '}
            <span className="font-medium">{line.po_number} / {line.line_number}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Qty Ordered:</span>{' '}
            <span className="font-medium">{line.quantity ? parseFloat(line.quantity).toFixed(0) : '-'}</span>
          </div>
          <div className="col-span-2">
            <span className="text-muted-foreground">Part:</span>{' '}
            <span className="font-medium">{line.part_number}</span>
            {line.description && <span className="text-muted-foreground"> — {line.description}</span>}
          </div>
        </div>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="cr5551-title">Title *</Label>
            <Input
              id="cr5551-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief title of the deviation"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cr5551-qty">Affected Quantity *</Label>
              <Input
                id="cr5551-qty"
                type="number"
                value={quantityAffected}
                onChange={(e) => setQuantityAffected(e.target.value)}
                placeholder="Number of units"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cr5551-devtype">Deviation Type *</Label>
              <Select value={deviationType} onValueChange={(v) => setDeviationType(v as ConcessionDeviationType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dimensional">Dimensional</SelectItem>
                  <SelectItem value="cosmetic">Cosmetic</SelectItem>
                  <SelectItem value="material">Material</SelectItem>
                  <SelectItem value="process">Process</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                  <SelectItem value="packaging">Packaging</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr5551-desc">Description *</Label>
            <Textarea
              id="cr5551-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detailed description of the deviation"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr5551-reason">Reason for Request *</Label>
            <Textarea
              id="cr5551-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being requested?"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cr5551-disposition">Proposed Disposition</Label>
            <Textarea
              id="cr5551-disposition"
              rows={2}
              value={proposedDisposition}
              onChange={(e) => setProposedDisposition(e.target.value)}
              placeholder="Use as-is, rework, sort, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="cr5551-risk">Risk Level *</Label>
              <Select value={riskLevel} onValueChange={(v) => setRiskLevel(v as ConcessionRiskLevel)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cr5551-riskj">Risk Justification</Label>
              <Input
                id="cr5551-riskj"
                value={riskJustification}
                onChange={(e) => setRiskJustification(e.target.value)}
                placeholder="Why this risk level?"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>) : `Submit ${requestType === 'production_permit' ? 'PE Request' : 'Concession'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
